import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';
import { translateErrorMessage } from '../utils/errorTranslations';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        showSuccess('Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.');
        setSubmitted(true);
        
        // Redirection vers la page de connexion après 5 secondes
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      } else {
        const data = await response.json();
        
        // Gérer les erreurs de validation (422) et autres erreurs
        let errorMessage = 'Une erreur est survenue';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const firstError = data.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        
        // Traduire et afficher l'erreur
        const translatedError = translateErrorMessage(errorMessage);
        showError(translatedError);
      }
    } catch (error) {
      showError('Erreur réseau, veuillez réessayer');
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
      {/* Élément décoratif en haut */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent"></div>
      
      <div className="max-w-md w-full space-y-3 z-10" style={{transform: 'translateY(-25%)'}}>
        <div className="flex flex-col items-center">
          {/* Logo pour tous les écrans */}
          <img 
            src="/images/logo-full.png" 
            alt="Mes Prix" 
            className="h-48 sm:h-48 lg:h-48 w-auto mx-auto" 
          />
          <h2 className="mt-1 text-center text-lg sm:text-xl font-extrabold text-gray-900">
            Mot de passe oublié
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Entrez votre email pour réinitialiser votre mot de passe
          </p>
        </div>
        
        {!submitted ? (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                disabled={loading}
              >
                {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center">
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700">
                Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.
              </p>
              <p className="text-sm text-green-700 mt-2">
                Redirection vers la page de connexion...
              </p>
            </div>
          </div>
        )}
        
        <div className="text-sm text-center">
          <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
            Retour à la connexion
          </Link>
        </div>
      </div>
      
      {/* Footer en bas */}
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100/80 to-transparent p-6">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">© 2025 Mes Prix - Tous droits réservés</p>
          <div className="flex justify-center space-x-4 text-xs text-gray-400">
            <span>Conditions d'utilisation</span>
            <span>•</span>
            <span>Politique de confidentialité</span>
            <span>•</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 